// src/pages/LoginPage.js
import React, { useState } from 'react';
import { useApp } from '../context/AppContext';

// ── Curated Showcase Books for Landing Page Preview ─────────────────────────
const FEATURED_BOOKS = [
  {
    id: 'c1',
    title: 'IPC Commentary',
    subtitle: 'Indian Penal Code 1860',
    edition: '6th Edition, 2024',
    author: 'Justice K.T. Thomas',
    badge: 'MUST READ',
    badgeBg: 'var(--rose)',
    bgGradient: 'linear-gradient(135deg, #2C2C35 0%, #1A1A22 100%)',
    accentColor: '#C97B77',
    pages: '1,842 Pages',
    tag: 'Criminal Law',
  },
  {
    id: 'c4',
    title: 'Constitution of India',
    subtitle: 'Commentary on the Constitution',
    edition: '7th Edition, 2025',
    author: 'Justice M.N. Venkatachaliah',
    badge: 'NEW EDITION',
    badgeBg: '#C4975A',
    bgGradient: 'linear-gradient(135deg, #1E2838 0%, #0F172A 100%)',
    accentColor: '#6B7FA0',
    pages: '3,210 Pages',
    tag: 'Constitutional Law',
  },
  {
    id: 'c2',
    title: 'CrPC Commentary',
    subtitle: 'Code of Criminal Procedure 1973',
    edition: '5th Edition, 2023',
    author: 'Justice R.V. Raveendran',
    badge: 'POPULAR',
    badgeBg: '#7A9E8E',
    bgGradient: 'linear-gradient(135deg, #1C2E2A 0%, #0D1C18 100%)',
    accentColor: '#7A9E8E',
    pages: '2,104 Pages',
    tag: 'Criminal Procedure',
  },
  {
    id: 'c3',
    title: 'Evidence Act Commentary',
    subtitle: 'Indian Evidence Act 1872',
    edition: '4th Edition, 2023',
    author: 'Batuk Lal',
    badge: 'UPDATED',
    badgeBg: '#9B8EC4',
    bgGradient: 'linear-gradient(135deg, #2D2338 0%, #1B1226 100%)',
    accentColor: '#9B8EC4',
    pages: '976 Pages',
    tag: 'Evidence',
  },
  {
    id: 'b1',
    title: 'Indian Penal Code Bare Act',
    subtitle: 'With State Amendments',
    edition: 'Current 2024',
    author: 'All India Reporter',
    badge: 'BARE ACT',
    badgeBg: '#555567',
    bgGradient: 'linear-gradient(135deg, #3A352E 0%, #221F1B 100%)',
    accentColor: '#C4A882',
    pages: '312 Pages',
    tag: 'Bare Act',
  },
  {
    id: 'c5',
    title: 'Civil Procedure Code',
    subtitle: 'Code of Civil Procedure 1908',
    edition: '3rd Edition, 2022',
    author: 'Mulla',
    badge: 'STANDARD',
    badgeBg: '#6B7FA0',
    bgGradient: 'linear-gradient(135deg, #252836 0%, #141724 100%)',
    accentColor: '#A8B8D0',
    pages: '1,654 Pages',
    tag: 'Civil Law',
  },
];

// ── Realistic 3D Book Cover Component ───────────────────────────────────────
const BookCover3D = ({ book, onClick }) => (
  <div 
    onClick={onClick}
    className="group relative cursor-pointer transition-all duration-300 transform hover:-translate-y-2 hover:scale-[1.02]"
    style={{ perspective: 1000 }}
  >
    {/* 3D shadow effect */}
    <div className="absolute -bottom-3 left-3 right-3 h-4 bg-black/20 rounded-full blur-md group-hover:bg-black/35 group-hover:blur-lg transition-all duration-300" />
    
    <div 
      className="relative rounded-xl overflow-hidden shadow-lg border border-white/10 flex flex-col justify-between p-5 min-h-[260px]"
      style={{ 
        background: book.bgGradient,
        boxShadow: '0 12px 28px -6px rgba(0,0,0,0.28), 0 0 0 1px rgba(255,255,255,0.08) inset'
      }}
    >
      {/* Book spine line styling */}
      <div className="absolute left-0 top-0 bottom-0 w-3 bg-white/15 border-r border-black/20" />
      <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-black/20" />

      {/* Top Header */}
      <div className="pl-3 relative z-10">
        <div className="flex items-center justify-between gap-2 mb-2">
          <span 
            className="text-[10px] font-bold px-2 py-0.5 rounded text-white tracking-wider uppercase font-ui"
            style={{ background: book.badgeBg }}
          >
            {book.badge}
          </span>
          <span className="text-[10px] text-white/60 font-ui tracking-wide">
            {book.pages}
          </span>
        </div>
        <span className="text-[11px] text-white/70 font-ui font-semibold block uppercase tracking-wider">
          {book.tag}
        </span>
      </div>

      {/* Center Title */}
      <div className="pl-3 py-4 relative z-10">
        <h4 className="font-display font-bold text-white text-lg leading-snug mb-1 group-hover:text-amber-200 transition-colors">
          {book.title}
        </h4>
        <p className="text-xs text-white/70 font-ui line-clamp-2">
          {book.subtitle}
        </p>
      </div>

      {/* Bottom Footer */}
      <div className="pl-3 pt-3 border-t border-white/15 relative z-10 flex items-center justify-between">
        <div>
          <div className="text-[11px] font-semibold text-white/90 font-ui">
            {book.author}
          </div>
          <div className="text-[10px] text-white/60 font-ui">
            {book.edition}
          </div>
        </div>
        <div className="w-7 h-7 rounded-full bg-white/15 group-hover:bg-white/30 flex items-center justify-center text-white text-xs transition-colors">
          →
        </div>
      </div>
    </div>
  </div>
);

// ── Brand Header (Inside Card) ──────────────────────────────────────────────
const BrandHeader = () => (
  <div className="text-center mb-6">
    <div className="mx-auto mb-3 flex items-center justify-center rounded-2xl shadow-md"
      style={{ width: 64, height: 64, background: 'rgba(255,255,255,.9)', border: '2px solid #E8B4B1' }}>
      <svg width="32" height="32" viewBox="0 0 30 30" fill="none">
        <rect x="5"  y="3"  width="13" height="19" rx="1.5" fill="rgba(201,123,119,0.85)"/>
        <rect x="10" y="3"  width="13" height="19" rx="1.5" fill="rgba(201,123,119,0.45)"/>
        <rect x="7"  y="7"  width="7"  height="1.2" rx=".6" fill="rgba(201,123,119,1)"/>
        <rect x="7"  y="10" width="5"  height="1.2" rx=".6" fill="rgba(201,123,119,.7)"/>
        <rect x="7"  y="13" width="6"  height="1.2" rx=".6" fill="rgba(201,123,119,.7)"/>
      </svg>
    </div>
    <div style={{ fontFamily: 'Playfair Display,serif', fontSize: 24, fontWeight: 700, color: 'var(--ink)' }}>
      AIR Reader
    </div>
    <div style={{ fontSize: 10, color: 'var(--ink-lt)', letterSpacing: 2.5, fontFamily: 'Nunito,sans-serif', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>
      All India Reporter · Est. 1914
    </div>
  </div>
);

// ── Spinner ───────────────────────────────────────────────────────────────────
const Spinner = () => (
  <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin .8s linear infinite' }}/>
);

// ── Input ─────────────────────────────────────────────────────────────────────
const Input = ({ type = 'text', placeholder, value, onChange, autoFocus }) => (
  <input
    type={type} placeholder={placeholder} value={value} onChange={onChange} autoFocus={autoFocus}
    style={{
      width: '100%', padding: '11px 14px', borderRadius: 10, fontSize: 13,
      border: '1px solid var(--border)', background: 'var(--off-white)',
      color: 'var(--ink)', fontFamily: 'Nunito,sans-serif', outline: 'none',
      boxSizing: 'border-box', marginBottom: 10,
    }}
  />
);

// ── Error Box ─────────────────────────────────────────────────────────────────
const ErrorBox = ({ msg }) => msg ? (
  <div style={{ background: 'var(--rose-pale)', border: '1px solid var(--rose-lt)', borderRadius: 8,
    padding: '8px 12px', fontSize: 12, color: 'var(--rose)', fontFamily: 'Nunito,sans-serif',
    marginBottom: 12, lineHeight: 1.5 }}>
    {msg}
  </div>
) : null;

// ── External SSO: verifying ───────────────────────────────────────────────────
const ExternalVerifying = () => (
  <div className="min-h-screen flex items-center justify-center p-4 relative"
    style={{ background: 'linear-gradient(155deg,#F0E4E3 0%,#E8EEF5 55%,#E4EEE8 100%)', fontFamily: 'Nunito,system-ui,sans-serif' }}>
    <div className="relative z-10 w-full max-w-sm animate-scale-in">
      <BrandHeader />
      <div className="rounded-2xl shadow-lg border p-8 text-center"
        style={{ background: 'rgba(255,255,255,.9)', backdropFilter: 'blur(8px)', borderColor: 'rgba(255,255,255,.5)' }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>{"🔐"}</div>
        <div style={{ fontFamily: 'Playfair Display,serif', fontSize: 18, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>
          Verifying your access
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif', marginBottom: 20, lineHeight: 1.6 }}>
          Connecting securely to AIR Online...
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: 32, height: 32, border: '3px solid var(--rose-pale)', borderTopColor: 'var(--rose)', borderRadius: '50%', animation: 'spin .8s linear infinite' }}/>
        </div>
      </div>
      <p className="text-center mt-4" style={{ fontSize: 11, color: 'var(--ink-faint)', fontFamily: 'Nunito,sans-serif' }}>
        DPDP Act 2023 compliant · India servers · No ads
      </p>
    </div>
    <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
  </div>
);

// ── External SSO: error ───────────────────────────────────────────────────────
const ExternalError = ({ error, onLoginManually }) => (
  <div className="min-h-screen flex items-center justify-center p-4 relative"
    style={{ background: 'linear-gradient(155deg,#F0E4E3 0%,#E8EEF5 55%,#E4EEE8 100%)', fontFamily: 'Nunito,system-ui,sans-serif' }}>
    <div className="relative z-10 w-full max-w-sm animate-scale-in">
      <BrandHeader />
      <div className="rounded-2xl shadow-lg border p-6"
        style={{ background: 'rgba(255,255,255,.9)', backdropFilter: 'blur(8px)', borderColor: 'rgba(255,255,255,.5)' }}>
        <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 12 }}>{"⚠️"}</div>
        <div style={{ fontFamily: 'Playfair Display,serif', fontSize: 18, fontWeight: 700, color: 'var(--ink)', textAlign: 'center', marginBottom: 8 }}>
          Authentication Failed
        </div>
        <ErrorBox msg={error} />
        <p style={{ fontSize: 12, color: 'var(--ink-mid)', textAlign: 'center', marginBottom: 16, lineHeight: 1.6, fontFamily: 'Nunito,sans-serif' }}>
          Please return to AIR Online and try again, or sign in below.
        </p>
        <button onClick={onLoginManually}
          className="w-full py-3 text-white rounded-xl font-bold border-0 cursor-pointer hover:opacity-90 shadow-md transition-all"
          style={{ background: 'var(--rose)', fontSize: 13, fontFamily: 'Nunito,sans-serif' }}>
          Sign in with Auth0 SSO
        </button>
      </div>
    </div>
  </div>
);

// ── Tab Button ────────────────────────────────────────────────────────────────
const TabBtn = ({ label, active, onClick }) => (
  <button onClick={onClick}
    style={{
      flex: 1, padding: '10px 0', fontSize: 12, fontWeight: 700, fontFamily: 'Nunito,sans-serif',
      border: 'none', cursor: 'pointer', borderBottom: active ? '2px solid var(--rose)' : '2px solid transparent',
      color: active ? 'var(--rose)' : 'var(--ink-lt)', background: 'transparent', transition: 'all .2s',
    }}>
    {label}
  </button>
);

// ── Email / Password Form ─────────────────────────────────────────────────────
const EmailForm = ({ apiBase, onSuccess }) => {
  const [step,     setStep]     = useState('email');   // email | password | setpass
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const handleEmailNext = async () => {
    if (!email.trim()) return setError('Please enter your email');
    setError(''); setLoading(true);
    try {
      const res  = await fetch(`${apiBase}/auth/check-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (data.exists) setStep('password');
      else             setStep('setpass');
    } catch (e) { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleLogin = async () => {
    if (!password) return setError('Please enter your password');
    setError(''); setLoading(true);
    try {
      const res  = await fetch(`${apiBase}/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Login failed');
      onSuccess(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleRegister = async () => {
    if (!password)              return setError('Please enter a password');
    if (password.length < 8)    return setError('Password must be at least 8 characters');
    if (password !== confirm)   return setError('Passwords do not match');
    setError(''); setLoading(true);
    try {
      const res  = await fetch(`${apiBase}/auth/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Registration failed');
      onSuccess(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <ErrorBox msg={error} />

      {/* Email step */}
      <Input type="email" placeholder="Email address" value={email}
        onChange={e => { setEmail(e.target.value); setError(''); }}
        autoFocus={step === 'email'} />

      {/* Password step — existing user */}
      {step === 'password' && (
        <>
          <div style={{ fontSize: 11, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif', marginBottom: 8 }}>
            Welcome back! Enter your password.
          </div>
          <Input type="password" placeholder="Password" value={password}
            onChange={e => { setPassword(e.target.value); setError(''); }} autoFocus />
        </>
      )}

      {/* Set password step — new user */}
      {step === 'setpass' && (
        <>
          <div style={{ fontSize: 11, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif', marginBottom: 8 }}>
            New account — set a password to continue.
          </div>
          <Input type="password" placeholder="Create password (min 8 chars)" value={password}
            onChange={e => { setPassword(e.target.value); setError(''); }} autoFocus />
          <Input type="password" placeholder="Confirm password" value={confirm}
            onChange={e => { setConfirm(e.target.value); setError(''); }} />
        </>
      )}

      {/* Action button */}
      <button
        onClick={step === 'email' ? handleEmailNext : step === 'password' ? handleLogin : handleRegister}
        disabled={loading}
        style={{
          width: '100%', padding: '11px 0', background: 'var(--rose)', color: 'white',
          border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700,
          fontFamily: 'Nunito,sans-serif', cursor: 'pointer', opacity: loading ? .7 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          boxShadow: '0 4px 12px rgba(201,123,119,0.3)',
        }}>
        {loading ? <><Spinner /><span>Please wait...</span></> :
          step === 'email'   ? 'Continue →' :
          step === 'password' ? 'Sign In →'  : 'Create Account →'}
      </button>

      {/* Back to email */}
      {step !== 'email' && (
        <button onClick={() => { setStep('email'); setPassword(''); setConfirm(''); setError(''); }}
          style={{ width: '100%', marginTop: 8, padding: '8px 0', background: 'transparent',
            border: 'none', fontSize: 12, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif', cursor: 'pointer' }}>
          ← Use different email
        </button>
      )}
    </div>
  );
};

// ── Phone OTP Form (UI only) ──────────────────────────────────────────────────
const PhoneForm = ({ apiBase, onSuccess }) => {
  const [step,    setStep]    = useState('phone');   // phone | otp
  const [phone,   setPhone]   = useState('');
  const [otp,     setOtp]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const handleSendOtp = async () => {
    if (!phone.trim() || phone.trim().length < 10) return setError('Please enter a valid 10-digit number');
    setError(''); setLoading(true);
    await new Promise(r => setTimeout(r, 800));
    setStep('otp');
    setLoading(false);
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) return setError('Please enter the OTP');
    setError(''); setLoading(true);
    await new Promise(r => setTimeout(r, 600));
    if (otp.trim() !== '123456') {
      setError('Invalid OTP. Use 123456 for testing.');
      setLoading(false);
      return;
    }
    setError('OTP flow backend not yet implemented. Coming soon!');
    setLoading(false);
  };

  return (
    <div>
      <ErrorBox msg={error} />

      {step === 'phone' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{ padding: '11px 12px', borderRadius: 10, border: '1px solid var(--border)',
              background: 'var(--off-white)', fontSize: 13, color: 'var(--ink-mid)', fontFamily: 'Nunito,sans-serif',
              whiteSpace: 'nowrap' }}>
              🇮🇳 +91
            </div>
            <input type="tel" placeholder="10-digit mobile number" value={phone}
              onChange={e => { setPhone(e.target.value.replace(/\D/g,'')); setError(''); }}
              maxLength={10} autoFocus
              style={{ flex: 1, padding: '11px 14px', borderRadius: 10, fontSize: 13,
                border: '1px solid var(--border)', background: 'var(--off-white)',
                color: 'var(--ink)', fontFamily: 'Nunito,sans-serif', outline: 'none' }}/>
          </div>
          <button onClick={handleSendOtp} disabled={loading}
            style={{ width: '100%', padding: '11px 0', background: 'var(--rose)', color: 'white',
              border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700,
              fontFamily: 'Nunito,sans-serif', cursor: 'pointer', opacity: loading ? .7 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: '0 4px 12px rgba(201,123,119,0.3)' }}>
            {loading ? <><Spinner /><span>Sending OTP...</span></> : 'Send OTP →'}
          </button>
        </>
      )}

      {step === 'otp' && (
        <>
          <div style={{ fontSize: 11, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif', marginBottom: 10, textAlign: 'center' }}>
            OTP sent to +91 {phone}
            <span style={{ color: 'var(--rose)', fontWeight: 700 }}> (use 123456 for testing)</span>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 12 }}>
            {[0,1,2,3,4,5].map(i => (
              <input key={i} type="tel" maxLength={1}
                value={otp[i] || ''}
                onChange={e => {
                  const val = e.target.value.replace(/\D/g,'');
                  const arr = otp.split('');
                  arr[i] = val;
                  const next = arr.join('').substring(0,6);
                  setOtp(next); setError('');
                  if (val && e.target.nextSibling) e.target.nextSibling.focus();
                }}
                onKeyDown={e => {
                  if (e.key === 'Backspace' && !otp[i] && e.target.previousSibling) e.target.previousSibling.focus();
                }}
                style={{ width: 42, height: 46, textAlign: 'center', fontSize: 18, fontWeight: 700,
                  borderRadius: 10, border: otp[i] ? '2px solid var(--rose)' : '1px solid var(--border)',
                  background: 'var(--off-white)', color: 'var(--ink)', outline: 'none', fontFamily: 'Nunito,sans-serif' }}
              />
            ))}
          </div>
          <button onClick={handleVerifyOtp} disabled={loading || otp.length < 6}
            style={{ width: '100%', padding: '11px 0', background: 'var(--rose)', color: 'white',
              border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700,
              fontFamily: 'Nunito,sans-serif', cursor: 'pointer', opacity: (loading || otp.length < 6) ? .7 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: '0 4px 12px rgba(201,123,119,0.3)' }}>
            {loading ? <><Spinner /><span>Verifying...</span></> : 'Verify OTP →'}
          </button>
          <button onClick={() => { setStep('phone'); setOtp(''); setError(''); }}
            style={{ width: '100%', marginTop: 8, padding: '8px 0', background: 'transparent',
              border: 'none', fontSize: 12, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif', cursor: 'pointer' }}>
            ← Change number
          </button>
        </>
      )}
    </div>
  );
};

// ── Auth Card Component (Form Container) ────────────────────────────────────
const AuthCard = ({ tab, setTab, handleAuth0Login, loading, apiBase, handleDirectSuccess }) => (
  <div 
    id="login-section"
    className="rounded-2xl shadow-xl border overflow-hidden backdrop-blur-md transition-all duration-300"
    style={{ background: 'rgba(255,255,255,0.92)', borderColor: 'rgba(255,255,255,0.7)', boxShadow: '0 20px 40px -10px rgba(44,44,53,0.15)' }}
  >
    <BrandHeader />

    {/* Tabs */}
    <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,0.02)' }}>
      <TabBtn label="🏛 AIR SSO"   active={tab==='sso'}   onClick={() => setTab('sso')} />
      <TabBtn label="✉️ Email"      active={tab==='email'} onClick={() => setTab('email')} />
      <TabBtn label="📱 Phone OTP" active={tab==='phone'} onClick={() => setTab('phone')} />
    </div>

    <div style={{ padding: 22 }}>
      <p style={{ fontSize: 12, color: 'var(--ink-mid)', textAlign: 'center', marginBottom: 16, lineHeight: 1.5, fontFamily: 'Nunito,sans-serif' }}>
        Sign in to open and read all your e-books.
      </p>

      {/* SSO Tab */}
      {tab === 'sso' && (
        <button 
          onClick={handleAuth0Login} 
          disabled={loading}
          className="w-full py-3 text-white rounded-xl font-bold border-0 cursor-pointer hover:opacity-95 transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
          style={{ background: 'var(--rose)', fontSize: 13, fontFamily: 'Nunito,sans-serif' }}
        >
          {loading ? <><Spinner /><span>Signing in...</span></> : <><span>🏛</span><span>Sign in with Auth0 SSO</span></>}
        </button>
      )}

      {/* Email Tab */}
      {tab === 'email' && (
        <EmailForm apiBase={apiBase} onSuccess={handleDirectSuccess} />
      )}

      {/* Phone Tab */}
      {tab === 'phone' && (
        <PhoneForm apiBase={apiBase} onSuccess={handleDirectSuccess} />
      )}
    </div>
  </div>
);

// ── Main LoginPage Landing Page Layout ──────────────────────────────────────
const LoginPage = ({ onLogin, onExternalSuccess }) => {
  const { extLoading, extError } = useApp() || {};
  const [tab, setTab] = useState('sso');
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');

  const apiBase = process.env.REACT_APP_API_URL || '';

  const handleAuth0Login = () => { setLoading(true); onLogin(); };

  const handleDirectSuccess = (data) => {
    try { localStorage.setItem('air_ext_auth0_token', data.access_token); } catch {}
    window.__AIR_ACCESS_TOKEN__ = data.access_token;
    if (onExternalSuccess) onExternalSuccess(data);
    else window.location.reload();
  };

  const scrollToLogin = () => {
    const el = document.getElementById('login-section');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // External SSO states
  if (extLoading) return <ExternalVerifying />;
  if (extError) return <ExternalError error={extError} onLoginManually={handleAuth0Login} />;

  const filteredBooks = activeFilter === 'All' 
    ? FEATURED_BOOKS 
    : FEATURED_BOOKS.filter(b => b.tag.toLowerCase().includes(activeFilter.toLowerCase()) || (activeFilter === 'Bare Acts' && b.tag === 'Bare Act'));

  return (
    <div className="min-h-screen bg-[#F5F2EE] text-[#2C2C35] font-ui relative overflow-x-hidden selection:bg-[#E8B4B1] selection:text-[#2C2C35]">
      {/* Background Decorative Gradient Blobs */}
      <div className="absolute top-0 right-0 w-[550px] h-[550px] rounded-full opacity-30 pointer-events-none blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(201,123,119,0.35) 0%, rgba(232,180,177,0.1) 70%, transparent 100%)', transform: 'translate(25%, -20%)' }} />
      <div className="absolute top-[40%] left-0 w-[600px] h-[600px] rounded-full opacity-25 pointer-events-none blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(107,127,160,0.3) 0%, rgba(168,184,208,0.1) 70%, transparent 100%)', transform: 'translate(-30%, 0%)' }} />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full opacity-20 pointer-events-none blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(122,158,142,0.3) 0%, transparent 70%)', transform: 'translate(20%, 20%)' }} />

      {/* Navigation Header */}
      <header className="sticky top-0 z-50 bg-[#F5F2EE]/80 backdrop-blur-md border-b border-[#E8E4DF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <div className="w-10 h-10 rounded-xl bg-white border border-[#E8B4B1] flex items-center justify-center shadow-sm">
              <svg width="22" height="22" viewBox="0 0 30 30" fill="none">
                <rect x="5"  y="3"  width="13" height="19" rx="1.5" fill="rgba(201,123,119,0.85)"/>
                <rect x="10" y="3"  width="13" height="19" rx="1.5" fill="rgba(201,123,119,0.45)"/>
                <rect x="7"  y="7"  width="7"  height="1.2" rx=".6" fill="rgba(201,123,119,1)"/>
                <rect x="7"  y="10" width="5"  height="1.2" rx=".6" fill="rgba(201,123,119,.7)"/>
              </svg>
            </div>
            <div>
              <span className="font-display font-bold text-lg text-[#2C2C35] leading-none block">
                AIR Reader
              </span>
              <span className="text-[9px] font-bold tracking-widest text-[#888899] uppercase font-ui block mt-0.5">
                Online E-Book Library
              </span>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-[#555567]">
            <a href="#books-section" className="hover:text-[#C97B77] transition-colors">All Books</a>
            <a href="#features-section" className="hover:text-[#C97B77] transition-colors">Features</a>
            <a href="#trust-section" className="hover:text-[#C97B77] transition-colors">About Reader</a>
          </nav>

          <button 
            onClick={scrollToLogin}
            className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-[#C97B77] hover:bg-[#b56965] shadow-sm transition-all cursor-pointer"
          >
            Sign In
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-12 pb-16 md:pt-20 md:pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column: Headline & Intro */}
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E8B4B1]/50 shadow-xs text-xs font-bold text-[#C97B77]">
              <span className="w-2 h-2 rounded-full bg-[#C97B77] animate-ping" />
              <span>Your Complete Online E-Book Reader</span>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2C2C35] leading-[1.15] tracking-tight">
              Read All Your Books Online, <span className="italic text-[#C97B77] font-serif">Anytime & Anywhere.</span>
            </h1>

            <p className="text-base sm:text-lg text-[#555567] leading-relaxed max-w-2xl font-ui">
              Open and read over 428+ e-books online with fast word search, easy page bookmarks, clickable footnotes, and color highlights.
            </p>

            {/* Quick Action Badges */}
            <div className="flex flex-wrap gap-4 pt-2">
              <button 
                onClick={scrollToLogin}
                className="px-6 py-3.5 rounded-xl text-sm font-bold text-white bg-[#C97B77] hover:bg-[#b56965] shadow-md hover:shadow-lg transition-all cursor-pointer flex items-center gap-2"
              >
                <span>Sign In to Read</span>
                <span>→</span>
              </button>
              <a 
                href="#books-section"
                className="px-6 py-3.5 rounded-xl text-sm font-bold text-[#2C2C35] bg-white border border-[#E8E4DF] hover:bg-[#FAFAF8] shadow-xs transition-all cursor-pointer flex items-center gap-2"
              >
                <span>Browse Books</span>
              </a>
            </div>

            {/* Stats Banner */}
            <div className="pt-8 border-t border-[#E8E4DF] grid grid-cols-3 gap-4 max-w-lg">
              <div>
                <div className="font-display font-bold text-2xl lg:text-3xl text-[#2C2C35]">428+</div>
                <div className="text-xs text-[#888899] font-semibold mt-0.5">Total E-Books</div>
              </div>
              <div>
                <div className="font-display font-bold text-2xl lg:text-3xl text-[#C97B77]">110+ Yrs</div>
                <div className="text-xs text-[#888899] font-semibold mt-0.5">Book History</div>
              </div>
              <div>
                <div className="font-display font-bold text-2xl lg:text-3xl text-[#2C2C35]">50,000+</div>
                <div className="text-xs text-[#888899] font-semibold mt-0.5">Active Readers</div>
              </div>
            </div>
          </div>

          {/* Right Column: Floating Auth Card */}
          <div className="lg:col-span-5 w-full max-w-md mx-auto">
            <AuthCard 
              tab={tab} 
              setTab={setTab} 
              handleAuth0Login={handleAuth0Login} 
              loading={loading} 
              apiBase={apiBase} 
              handleDirectSuccess={handleDirectSuccess} 
            />
          </div>

        </div>
      </section>

      {/* Featured Books Library Catalog Showcase Section */}
      <section id="books-section" className="py-16 md:py-24 bg-white/60 border-y border-[#E8E4DF] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <div className="text-xs font-bold text-[#C97B77] uppercase tracking-wider mb-1 font-ui">
                E-Book Collection
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#2C2C35]">
                Books You Can Read
              </h2>
              <p className="text-sm text-[#555567] mt-1 font-ui">
                Select any e-book below to start reading its chapters and notes.
              </p>
            </div>

            {/* Filter Pills */}
            <div className="flex flex-wrap gap-2">
              {['All', 'Criminal Law', 'Constitutional Law', 'Evidence', 'Bare Acts'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer ${
                    activeFilter === filter
                      ? 'bg-[#C97B77] text-white shadow-sm'
                      : 'bg-white text-[#555567] border border-[#E8E4DF] hover:bg-[#F5F2EE]'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          {/* 3D Book Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBooks.map((book) => (
              <BookCover3D 
                key={book.id} 
                book={book} 
                onClick={scrollToLogin} 
              />
            ))}
          </div>

          {/* Bottom Banner inside showcase */}
          <div className="mt-12 text-center bg-[#F9EDEC] border border-[#E8B4B1]/40 rounded-2xl p-6 max-w-3xl mx-auto">
            <h3 className="font-display font-bold text-lg text-[#2C2C35]">
              Looking for a specific e-book?
            </h3>
            <p className="text-xs text-[#555567] mt-1 font-ui">
              Our complete online library lets you search across all chapters, topics, and book titles.
            </p>
            <button 
              onClick={scrollToLogin}
              className="mt-4 px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-[#C97B77] hover:bg-[#b56965] shadow-sm transition-all cursor-pointer"
            >
              Sign In to Read All Books
            </button>
          </div>

        </div>
      </section>

      {/* Platform Features Section */}
      <section id="features-section" className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="text-xs font-bold text-[#C97B77] uppercase tracking-wider mb-1 font-ui">
            Easy E-Book Features
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#2C2C35]">
            Why You Will Love Reading Here
          </h2>
          <p className="text-sm text-[#555567] mt-2 font-ui">
            Everything you need for a smooth and comfortable e-book reading experience.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          
          {/* Feature 1 */}
          <div className="bg-white rounded-2xl p-8 border border-[#E8E4DF] shadow-xs hover:shadow-md transition-all">
            <div className="w-12 h-12 rounded-xl bg-[#F9EDEC] text-[#C97B77] flex items-center justify-center text-2xl mb-6">
              🔍
            </div>
            <h3 className="font-display font-bold text-xl text-[#2C2C35] mb-2">
              Instant Book Search
            </h3>
            <p className="text-xs text-[#555567] leading-relaxed font-ui">
              Search any word, topic, or chapter inside your e-books in seconds with highlighted results.
            </p>
          </div>

          {/* Feature 2 */}
          <div className="bg-white rounded-2xl p-8 border border-[#E8E4DF] shadow-xs hover:shadow-md transition-all">
            <div className="w-12 h-12 rounded-xl bg-[#EEF2F8] text-[#6B7FA0] flex items-center justify-center text-2xl mb-6">
              📌
            </div>
            <h3 className="font-display font-bold text-xl text-[#2C2C35] mb-2">
              Easy Page Notes
            </h3>
            <p className="text-xs text-[#555567] leading-relaxed font-ui">
              Click footnote numbers to read extra information instantly without leaving your current page.
            </p>
          </div>

          {/* Feature 3 */}
          <div className="bg-white rounded-2xl p-8 border border-[#E8E4DF] shadow-xs hover:shadow-md transition-all">
            <div className="w-12 h-12 rounded-xl bg-[#EBF4F0] text-[#7A9E8E] flex items-center justify-center text-2xl mb-6">
              ✍️
            </div>
            <h3 className="font-display font-bold text-xl text-[#2C2C35] mb-2">
              Highlights & Bookmarks
            </h3>
            <p className="text-xs text-[#555567] leading-relaxed font-ui">
              Highlight your favorite lines in color, write personal notes, and save bookmarks as you read.
            </p>
          </div>

        </div>
      </section>

      {/* Trust & Compliance Section */}
      <section id="trust-section" className="py-12 bg-[#2C2C35] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center md:text-left items-center">
            
            <div className="md:col-span-1">
              <div className="font-display font-bold text-xl text-[#E8B4B1]">
                AIR Reader
              </div>
              <div className="text-xs text-white/60 font-ui mt-1">
                Trusted by thousands of readers across India.
              </div>
            </div>

            <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="flex items-center gap-3 bg-white/5 p-4 rounded-xl border border-white/10">
                <div className="text-2xl">🛡️</div>
                <div className="text-left">
                  <div className="text-xs font-bold text-white">100% Private & Secure</div>
                  <div className="text-[10px] text-white/60">Your notes and account stay safe</div>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-white/5 p-4 rounded-xl border border-white/10">
                <div className="text-2xl">⚡</div>
                <div className="text-left">
                  <div className="text-xs font-bold text-white">Fast Loading</div>
                  <div className="text-[10px] text-white/60">E-books open instantly on any screen</div>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-white/5 p-4 rounded-xl border border-white/10">
                <div className="text-2xl">📖</div>
                <div className="text-left">
                  <div className="text-xs font-bold text-white">Complete Book Pages</div>
                  <div className="text-[10px] text-white/60">Full text with exact chapter pages</div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#F5F2EE] border-t border-[#E8E4DF] text-center text-xs text-[#888899]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 font-ui">
          <div>
            © {new Date().getFullYear()} All India Reporter (AIR) Law Academy & Research Centre. All rights reserved.
          </div>
          <div className="flex items-center gap-6">
            <span>Terms of Service</span>
            <span>Privacy Policy</span>
            <span>Support</span>
          </div>
        </div>
      </footer>

      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
};

export { LoginPage };
export default LoginPage;